<template lang="pug">
.option-target-family-list-modal
  // modal
  b-modal(v-model="_value" size="xl" @ok="ok" no-close-on-backdrop no-close-on-esc)
    template(#modal-header="{ ok }")
      .option-target-family-list-modal-header
        .d-flex.justify-content-start.align-items-center
          .d-flex.justify-content-start
            span.menu-title {{ _title }}
          .d-flex.justify-content-end.flex-fill(v-if="_showPdfDownloadBtn == true")
            button.btn(@click="onPdfDownload" type="button" style="width: 162px; max-width: 162px;") PDFダウンロード
          .d-flex.justify-content-end.flex-fill(v-if="_showCsvDownloadBtn == true")
            button.btn(@click="onCsvDownload" type="button" style="width: 162px; max-width: 162px;") CSVダウンロード
      button.close(type="button" @click="ok") ×

    .option-target-family-list-modal-body
      OptionTargetFamilyTable(
        v-model="_items"
        :fields="fields"
        :isLoading="isLoading"
      )

    template(#modal-footer="{ ok }")
      b-button.btn.modal-ok-btn(@click="ok") 閉じる

  SendDocumentPdfSelectModal(
    v-model="showPdfSelectModal"
    @submit="handlePdfSelectSubmit"
  )
  PageLoading(:isBusy="isPdfGenerating" message="PDFを作成中...")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";

import { userStore, fontsStore } from "@/store/store";
import Setting from "../../../../public/setting.json";
import S3UrlService from "@/commons/services/S3Url";
import PdfUtil from "@/commons/utils/PdfUtil";
import { errorHandle } from "@/errorHandler";

import { SendDocumentsOptionTargetFamiliesPdf } from "@/commons/utils/SendDocumentsOptionTargetFamiliesPdf";

import OptionTargetFamilyTable, { OptionTargetFamilyTableField, OptionTargetFamilyTableItem } from "@/pc/containers/sendDocuments/OptionTargetFamilyTable.vue";
import SendDocumentPdfSelectModal from "@/pc/components/modals/SendDocumentPdfSelectModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import { SendDocumentFile, SendDocumentGetModel, Unit } from "@/commons/services/SendDocuments";
import { FacilityGetModel } from "@/commons/services/Facilities";

/**
 * ### 文書送付、確認・同意ユーザー一覧モーダル
 */
@Component({
  components: {
    OptionTargetFamilyTable,
    SendDocumentPdfSelectModal,
    PageLoading
  }
})
export default class OptionTargetFamilyListModal extends Vue {
  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;
  /** テーブル行情報 */
  @Prop({ required: true })
  items!: Array<OptionTargetFamilyTableItem>;
  /** 読込み中か否か */
  @Prop({ required: true })
  isLoading!: boolean;
  /** モーダル種別(同意か確認か) */
  @Prop()
  modalType!: OptionTargetFamilyListModalType;
  /** PDFで使用する文書送付 */
  @Prop()
  sendDocument!: SendDocumentGetModel;

  /** テーブル列情報 */
  fields: Array<OptionTargetFamilyTableField> = [];

  /** 施設名 */
  @Getter("getFacility") facility!: FacilityGetModel;

  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** PDF出力形式選択モーダル表示フラグ */
  showPdfSelectModal: boolean = false;
  /** PDF作成中ローディングフラグ */
  isPdfGenerating: boolean = false;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _items(): Array<OptionTargetFamilyTableItem> {
    return this.items;
  }

  set _items(newValue: Array<OptionTargetFamilyTableItem>) {
    this.$emit("update:items", newValue);
    return;
  }

  get _title(): string {
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.AGREEMENT: {
        return "同意済一覧";
      }
      case OptionTargetFamilyListModalType.NON_AGREEMENT: {
        return "未同意一覧";
      }
      case OptionTargetFamilyListModalType.CONFIRM: {
        return "確認済一覧";
      }
      case OptionTargetFamilyListModalType.NON_CONFIRM: {
        return "未確認一覧";
      }
      default: {
        return "";
      }
    }
  }

  get _showPdfDownloadBtn(): boolean {
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.AGREEMENT:
      case OptionTargetFamilyListModalType.CONFIRM: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  get _showCsvDownloadBtn(): boolean {
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.NON_AGREEMENT: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  get _sendDocumentDatetime(): string {
    if (this.sendDocument == null) {
      return "";
    }

    const datetime: string = this.$dateUtil.convertNumberToString(this.sendDocument.sendDatetime as number, "yyyy年 mm月 dd日 hh:MM", false);

    return datetime;
  }

  /**
   * ### vue lifesycle mounted
   */
  mounted(): void {
    let fields: Array<OptionTargetFamilyTableField> = this.createFields();
    this.fields = fields;

    return;
  }

  /**
   * ### 列名を作成する
   */
  private createFields(): Array<OptionTargetFamilyTableField> {
    let fields: Array<OptionTargetFamilyTableField> = [];

    fields.push({ key: "groupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "residentName", label: "利用者名（部屋名）", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "familyName", label: "家族名（続柄）", sortable: false, sortDirection: "desc", class: "family-name" });
    fields.push({ key: "familyTel", label: "電話番号", sortable: false, sortDirection: "desc", class: "family-tel" });
    fields.push({ key: "datetime", label: "返信日時", sortable: false, sortDirection: "desc", class: "datetime" });
    fields.push({ key: "message", label: "返信時の連絡事項", sortable: false, sortDirection: "desc", class: "message" });

    return fields;
  }

  ok(): void {
    this.$emit("ok");
    return;
  }

  /**
   * ### PDFダウンロードボタン押下イベント
   */
  async onPdfDownload(): Promise<void> {
    this.showPdfSelectModal = true;
    return;
  }

  /**
   * ### PDF出力形式選択後のハンドラー
   */
  async handlePdfSelectSubmit(outputType: "list" | "detail"): Promise<void> {
    if (outputType === "list") {
      await this.createAndDownloadPdf();
    } else if (outputType === "detail") {
      await this.createAndDownloadDetailPdf();
    }
    return;
  }

  /**
   * ### PDFを作成する（出力A：一覧形式）
   */
  private async createAndDownloadPdf(): Promise<void> {
    this.isPdfGenerating = true;
    try {
      const fileName: string = this.createPdfFileName();
      const font: string = await fontsStore.getPdfFont({ userStore });
      const sendDocumentsReportPdfUtil: SendDocumentsOptionTargetFamiliesPdf = new SendDocumentsOptionTargetFamiliesPdf(font);
      await sendDocumentsReportPdfUtil.createAndDownloadPdf(
        this._items,
        this.facility.name,
        this.sendDocument ? this.sendDocument.title : "",
        this._sendDocumentDatetime,
        this.modalType,
        fileName
      );
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isPdfGenerating = false;
    }

    return;
  }

  /**
   * ### PDFを作成する（出力B：詳細形式）
   */
  private async createAndDownloadDetailPdf(): Promise<void> {
    this.isPdfGenerating = true;
    try {
      const fileName: string = this.createPdfFileName();
      const font: string = await fontsStore.getPdfFont({ userStore });
      const imagesMap = await this.loadAllDetailImages();
      const sendDocumentsReportPdfUtil: SendDocumentsOptionTargetFamiliesPdf = new SendDocumentsOptionTargetFamiliesPdf(font);
      sendDocumentsReportPdfUtil.createAndDownloadDetailPdf(
        this._items,
        this.facility.name,
        this.sendDocument ? this.sendDocument.title : "",
        this._sendDocumentDatetime,
        this.modalType,
        fileName,
        imagesMap
      );
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isPdfGenerating = false;
    }
    return;
  }

  /**
   * ### 出力B用の全同意者の添付書類画像を取得する
   */
  private async loadAllDetailImages(): Promise<Map<string, Array<string>>> {
    const imagesMap = new Map<string, Array<string>>();
    if (!this.sendDocument || !this.sendDocument.fileUrls || this.sendDocument.fileUrls.length === 0) {
      return imagesMap;
    }

    for (const item of this._items) {
      const docFiles: Array<SendDocumentFile> = this.getSendDocumentFilesForItem(item);
      const dataUrls: Array<string> = [];
      for (const docFile of docFiles) {
        try {
          const file: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, docFile.url, docFile.name);
          if (file.type === "application/pdf") {
            const result = await PdfUtil.convertFileToJpeg(file, 1);
            if (result && result.dataUrl) {
              const portraitUrl = await PdfUtil.ensurePortraitDataUrl(result.dataUrl);
              dataUrls.push(portraitUrl);
            }
          } else if (file.type.startsWith("image/")) {
            const dataUrl = await PdfUtil.convertDataUrl(file);
            if (dataUrl) {
              const portraitUrl = await PdfUtil.ensurePortraitDataUrl(dataUrl);
              dataUrls.push(portraitUrl);
            }
          }
        } catch (e) {
          console.error(`Failed to load file: ${docFile.name}`, e);
          const systemError = e as any;
          if (systemError && (systemError.statusCode === 401 || systemError.statusCode === 403)) {
            throw e;
          }
        }
      }
      if (dataUrls.length > 0) {
        imagesMap.set(item.residentsSortKey, dataUrls);
        imagesMap.set(String(item.id), dataUrls);
      }
    }
    return imagesMap;
  }

  /**
   * ### 対象の同意者項目に紐づく添付ファイルを取得する
   */
  private getSendDocumentFilesForItem(item: OptionTargetFamilyTableItem): Array<SendDocumentFile> {
    if (!this.sendDocument || !this.sendDocument.fileUrls || this.sendDocument.fileUrls.length === 0) {
      return [];
    }
    if (this.sendDocument.unit === Unit.SAME || this.sendDocument.fileUrls.length === 1) {
      return this.sendDocument.fileUrls[0].files || [];
    }
    const matched = this.sendDocument.fileUrls.find((fUrl) => fUrl.sortKey === item.residentsSortKey || fUrl.sortKey === String(item.id));
    if (matched && matched.files) {
      return matched.files;
    }
    return this.sendDocument.fileUrls[0] ? this.sendDocument.fileUrls[0].files || [] : [];
  }

  /**
   * ### PDFのファイル名を作成する
   */
  private createPdfFileName(): string {
    let fileName: string = "";
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.AGREEMENT: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-同意済一覧.pdf`;
        break;
      }
      case OptionTargetFamilyListModalType.CONFIRM: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-確認済一覧.pdf`;
        break;
      }
      default: {
        fileName = "";
        break;
      }
    }

    return fileName;
  }

  /**
   * ### CSVダウンロードボタン押下イベント
   */
  onCsvDownload(): void {
    this.createAndDownloadCsv();
    return;
  }

  /**
   * ### CSVを作成してダウンロードさせる
   */
  private createAndDownloadCsv(): void {
    const csv: string = this.createCsv(this._items);
    const fileName: string = this.createCsvFileName();
    this.downloadCsv(csv, fileName);
    return;
  }

  /**
   * ### 未同意家族のCSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   */
  private createCsv(items: Array<OptionTargetFamilyTableItem>): string {
    const csv: string[] = [];

    const header: string[] = ["利用者グループ", "利用者名", "部屋名", "家族名", "続柄", "電話番号"];
    csv.push(`\ufeff${header.join(",")}`);

    for (const item of items) {
      const body: string[] = [];
      body.push(item.groupName);
      body.push(item.name);
      body.push(item.room);
      body.push(item.familyName);
      body.push(item.familyRelation);
      body.push(item.familyTel);

      csv.push(body.join(","));
    }

    return csv.join("\n");
  }

  /**
   * ### CSVのファイル名を作成する
   */
  private createCsvFileName(): string {
    let fileName: string = "";
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.NON_AGREEMENT: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-未同意一覧.csv`;
        break;
      }
      default: {
        fileName = "";
        break;
      }
    }

    return fileName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  private downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    return;
  }
}

export enum OptionTargetFamilyListModalType {
  UNKNOWN = -1,
  AGREEMENT = 0,
  NON_AGREEMENT = 1,
  CONFIRM = 2,
  NON_CONFIRM = 3
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.option-target-family-list-modal {
  .pdf-contetns {
    // A4用紙相当のサイズを取る
    width: 159mm;
    height: 209mm;
    padding: 10mm;

    .datetime {
      font-size: 10px;
    }
    .title {
      font-size: 18px;
      font-weight: bold;
    }
    .send-document-infos {
      font-size: 10px;
    }

    .contents {
      font-size: 10px;
      margin-left: auto;
      margin-right: auto;

      table {
        width: 100%;
        border: 1px solid $border-darkgray;

        thead {
          background-color: $bg-header;
          text-align: center;
        }
        th,
        td {
          padding: 0 0 4px 4px;
          border: 1px solid $border-darkgray;
        }
        .resident-group,
        .resident-name,
        .family-name {
          width: 220px;
        }
        .reply-datetime {
          width: 200px;
        }
      }
      margin-bottom: 40px;
    }
  }
}

.pageout {
  position: fixed;
  z-index: -100;
}
</style>
